import { useCountry } from "./useCountry";

// `tel` is the dialable href (kept unformatted — no spaces/hyphens — so it
// dials reliably and shows clean on hover/long-press across devices).
// `display` is the human-friendly text shown inside the link.
export const SUPPORT_NUMBERS = {
  US: { tel: "tel:+18007494920", display: "+1 (800) 749-4920" },
  GB: { tel: "tel:+448008021575", display: "+44 800 802 1575" },
};

// Default / fallback region used when the country is unknown or unmapped.
export const DEFAULT_REGION = "US";

/**
 * Returns the support number for a given country code.
 * Falls back to the US number for unknown / undetected countries.
 *
 * @param {string} [country] ISO country code from the `country` cookie.
 * @returns {{ tel: string, display: string }}
 */
export function getSupportNumber(country) {
  const normalized = (country || "").toUpperCase();
  return SUPPORT_NUMBERS[normalized] || SUPPORT_NUMBERS[DEFAULT_REGION];
}

/**
 * React hook returning the support number for the current visitor.
 * Reads the visitor country from the `country` cookie via `useCountry()`.
 *
 * Note: on the server and the first client render this returns the US number
 * (the `useCountry` default); after hydration it updates to the correct region.
 *
 * @returns {{ tel: string, display: string }}
 */
export function useSupportNumber() {
  const country = useCountry();
  return getSupportNumber(country);
}

/**
 * Localizes a data-driven contact object (e.g. `data.contact` from page JSON)
 * for the visitor's country. Only swaps the number when the supplied contact
 * is the US support number — any other contact is returned untouched, so
 * page-specific contact details are never accidentally overwritten.
 *
 * @param {string} country ISO country code (e.g. "GB", "US").
 * @param {{ href?: string, text?: string }} [contact] Contact object from JSON.
 * @returns {{ href?: string, text?: string }} Possibly-localized contact object.
 */
export function localizeSupportContact(country, contact) {
  const usDigits = telDigits(SUPPORT_NUMBERS[DEFAULT_REGION].tel);
  if (!contact || telDigits(contact.href) !== usDigits) {
    return contact;
  }
  const number = getSupportNumber(country);
  return { ...contact, href: number.tel, text: number.display };
}

/** Reduce a tel href/number to its bare digits for format-agnostic matching. */
function telDigits(value) {
  return String(value || "").replace(/\D/g, "");
}

// IDs of the header support-number anchors the pre-paint script localizes.
const SUPPORT_NUMBER_ANCHOR_IDS = [
  "header-cta-number",
  "header-mobile-number",
  "header-login-number",
  "header-minimal-number",
  "community-call",
];

export const supportNumberInlineScript = `(function () {
  try {
    var m = document.cookie.match(/(?:^|;\\s*)country=([^;]+)/);
    if (!m || decodeURIComponent(m[1]).toUpperCase() !== "GB") return;
    var US = ${JSON.stringify(telDigits(SUPPORT_NUMBERS.US.tel))};
    var UK_TEL = ${JSON.stringify(SUPPORT_NUMBERS.GB.tel)};
    var UK_TEXT = ${JSON.stringify(SUPPORT_NUMBERS.GB.display)};
    var ids = ${JSON.stringify(SUPPORT_NUMBER_ANCHOR_IDS)};
    var digits = function (s) { return (s || "").replace(/\\D/g, ""); };
    for (var i = 0; i < ids.length; i++) {
      var el = document.getElementById(ids[i]);
      if (el && digits(el.getAttribute("href")) === US) {
        el.setAttribute("href", UK_TEL);
        // Only swap the visible text when it is the number itself; anchors
        // whose label is something else (e.g. "Call") keep their content.
        if (digits(el.textContent) === US) {
          el.textContent = UK_TEXT;
        }
      }
    }
  } catch (e) {}
})();`;
